import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    ChatBubbleLeftRightIcon,
    XMarkIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";
import { Button } from "components/ui/button";
import dayjs from "dayjs";
import { getCtoNotes, createCtoNote, type CtoNote } from "../api/notes";

interface Props {
    cnNumber: string;
}

export const NotesModal: React.FC<Props> = ({ cnNumber }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [notes, setNotes] = useState<CtoNote[]>([]);
    const [message, setMessage] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);

    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        });
    }, [isOpen, notes]);


    const currentUser = useMemo(() => {
        try {
            const raw = localStorage.getItem("user_data");
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed.full_name || null;
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        const loadNotes = async () => {
            try {
                setLoading(true);
                const data = await getCtoNotes(cnNumber);
                setNotes(data.notes || []);
            } catch (err) {
                console.error("Failed to load notes", err);
            } finally {
                setLoading(false);
            }
        };

        loadNotes();
    }, [isOpen, cnNumber]);

    const handleSend = async () => {
        if (!message.trim()) return;

        try {
            setLoading(true);
            await createCtoNote(cnNumber, {
                content: message.trim(),
            });

            const data = await getCtoNotes(cnNumber);
            setNotes(data.notes || []);
            setMessage("");
        } catch (err) {
            console.error("Failed to send note", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                className="h-7 px-2.5 py-1 text-xs bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200"
                onClick={() => setIsOpen(true)}
            >
                <ChatBubbleLeftRightIcon className="h-4 w-4" />
            </Button>

            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                    <div className="w-[600px] max-w-[90vw] h-[600px] bg-white rounded-xl shadow-2xl flex flex-col">
                        <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50 rounded-t-xl">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-800">
                                    CTO Notes
                                </h3>
                                <p className="text-xs text-gray-500">CN: {cnNumber}</p>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-1 rounded-md hover:bg-gray-200 transition"
                            >
                                <XMarkIcon className="h-5 w-5 text-gray-600" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto scroll-smooth px-5 py-4 space-y-4 bg-[#f8fafc]">
                            {loading && (
                                <div className="text-center text-xs text-gray-400">
                                    Loading notes…
                                </div>
                            )}

                            {!loading && notes.length === 0 && (
                                <div className="flex h-full items-center justify-center">
                                    <span className="text-xs text-gray-400">
                                        No notes yet
                                    </span>
                                </div>
                            )}

                            {notes.map((note, idx) => {
                                const isSelf =
                                    currentUser && note.created_by === currentUser;

                                return (
                                    <div
                                        key={idx}
                                        className={`flex items-start gap-2 ${isSelf ? "justify-end" : "justify-start"
                                            }`}
                                    >
                                        {!isSelf && (
                                            <div className="w-10 flex flex-col items-center flex-shrink-0">
                                                <UserCircleIcon className="h-6 w-6 text-gray-400" />
                                                <span className="mt-0.5 text-[10px] text-gray-400 text-center break-words leading-tight">
                                                    {note.created_by}
                                                </span>
                                            </div>
                                        )}

                                        <div
                                            className={`rounded-2xl px-4 py-2.5 text-xs shadow-sm ${isSelf
                                                    ? "bg-blue-500 text-white"
                                                    : "bg-white text-gray-800 border border-gray-200"
                                                }`}
                                            style={{ maxWidth: "80%" }}
                                        >
                                            <p className="whitespace-pre-wrap leading-relaxed">
                                                {note.content}
                                            </p>
                                            <div className="mt-1 text-[10px] opacity-70 text-right">
                                                {dayjs(note.created_at).format("DD MMM, HH:mm")}
                                            </div>
                                        </div>

                                        {isSelf && (
                                            <div className="w-10 flex flex-col items-center flex-shrink-0">
                                                <UserCircleIcon className="h-6 w-6 text-blue-500" />
                                                <span className="mt-0.5 text-[10px] text-blue-500 text-center">
                                                    You
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        <div className="border-t bg-white px-4 py-3 flex items-center gap-2 rounded-b-xl">
                            <input
                                className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-xs
                           focus:outline-none focus:ring-2 focus:ring-blue-400
                           placeholder-gray-400"
                                placeholder="Type a note…"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                            />
                            <Button
                                size="sm"
                                className="rounded-full px-4"
                                onClick={handleSend}
                            >
                                Send
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
